/**
 * useTilt — Apple-style 3D perspective tilt on cards.
 *
 * Applies transform directly on the DOM node for 60fps (no React re-render).
 * Uses motion/react `animate` for spring-back on mouse leave.
 * No-op on touch devices (pointer: coarse) and prefers-reduced-motion.
 *
 * IMPORTANT: The ref should target an inner <div> separate from any
 * motion.div with stagger variants — direct style.transform conflicts
 * with motion's internal transforms.
 */

import { useCallback, useRef } from 'react';
import { animate } from 'motion/react';

interface TiltOptions {
  maxTilt?: number;
  perspective?: number;
  scale?: number;
}

interface TiltReturn {
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
}

const isCoarse =
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: coarse)').matches;

const prefersReduced =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useTilt(
  ref: React.RefObject<HTMLElement | null>,
  { maxTilt = 4, perspective = 800, scale = 1.02 }: TiltOptions = {},
): TiltReturn {
  const disabled = useRef(isCoarse || prefersReduced);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (disabled.current || !ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1; // -1..+1
      const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      const rx = -ny * maxTilt; // rotateX
      const ry = nx * maxTilt;  // rotateY
      ref.current.style.transform = `perspective(${perspective}px) rotateX(${rx}deg) rotateY(${ry}deg) scale(${scale})`;
    },
    [ref, maxTilt, perspective, scale],
  );

  const onMouseLeave = useCallback(() => {
    if (disabled.current || !ref.current) return;
    const el = ref.current;
    animate(el, { transform: 'perspective(800px) rotateX(0deg) rotateY(0deg) scale(1)' }, {
      type: 'spring',
      stiffness: 300,
      damping: 25,
    });
  }, [ref]);

  return { onMouseMove, onMouseLeave };
}
